import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { updateConfig } from "../../../../store/slices/User/OfficeDelivery/officeDeliverySlice";
import { toast } from "react-toastify";
import { createOfficeDelivery } from "../api";
import { useGetOfficeDeliveryBasicDataQuery } from "../../../../store/queries/User";
import useValidations from "../../../../utils/hooks/useValidations";
import dayjs from "dayjs";

const useAddOfficeDelivery = (handleCreateClick, refetch) => {
  const dispatch = useDispatch();
  const { englishRegex, arabicRegex } = useValidations();
  const { data: basicData = {}, isLoading: basicLoading } = useGetOfficeDeliveryBasicDataQuery();
  const { isEdit, editOfficeDeliveryData } = useSelector(
    (state) => state.officeDelivery
  );

  const { isLoading } = useSelector((state) => state.officeDelivery);
  const validation = Yup.object({
    office_name: Yup.string()
      .trim()
      .matches(englishRegex, "Enter valid Office Name")
      .required("Enter Office Name"),
    alias_office_name: Yup.string()
      .trim()
      .matches(arabicRegex, "Enter valid Office Name")
      .required("Enter Office Name"),
    office_address: Yup.string()
      .trim()
      .matches(englishRegex, "Enter valid Office Address")
      .required("Enter Office Address"),
    alias_office_address: Yup.string()
      .trim()
      .matches(arabicRegex, "Enter valid Office Address")
      .required("Enter Office Address"),
    stores: Yup.array()
  .min(1, "Select at least one store")
  .required("Select Store"),
  });

  const formik = useFormik({
    initialValues: {
      office_name: "",
      alias_office_name: "",
      status: 2,
      stores: [],
      office_address:"",
      alias_office_address:""
    },
    validationSchema: validation,
    onSubmit: (values) => {
      let data = {
        office_name: values?.office_name ?? "",
        alias_office_name: values?.alias_office_name ?? "",
        status: values?.status ?? 2,
        customer_keys: values.stores?.map((store) => store.customer_key) ?? [],
        store_names: values.stores?.map((store) => store.name) ?? [],
        office_address: values?.office_address ?? "",
        alias_office_address: values?.alias_office_address ?? "",
      };
      let newObj = Object.entries(data);
      let subData = newObj
        .filter(
          (item) => item[1] !== undefined && item[1] !== "" && item[1] !== null
        )
        .reduce((a, v) => ({ ...a, [v[0]]: v[1] }), {});
      dispatch(
        updateConfig((state) => {
          state.isLoading = true;
        })
      );

      if (!isEdit) {
        createOfficeDelivery(subData).then((response) => {
          if (response?.data?.status_code === 200) {
            toast.success(`Office Delivery created successfully`);
            refetch();
            handleCreateClick();
            dispatch(
              updateConfig((state) => {
                state.clearSelection = true;
                state.isLoading = false;
              })
            );
          } else if (response?.data?.status_code === 422 && response?.data?.errors) {
            Object.keys(response?.data?.errors).forEach((field) => {
              formik.setFieldError(field, response?.data?.errors[field]);
            });
            dispatch(
              updateConfig((state) => {
                state.isLoading = false;
              })
            );
          } else {
            toast.error(`Something went wrong!`);
            handleCreateClick();
            dispatch(
              updateConfig((state) => {
                state.clearSelection = true;
                state.isLoading = false;
              })
            );
          }
        });
      } else {
        let updatePopularSearchData = {
          id: editOfficeDeliveryData?._id,
          ...subData,
        };

        createOfficeDelivery(updatePopularSearchData).then((response) => {
          if (response?.data?.status_code === 200) {
            toast.success(`Office Delivery updated successfully`);
            refetch();
            handleCreateClick();
            dispatch(
              updateConfig((state) => {
                state.clearSelection = true;
                state.isLoading = false;
                state.editOfficeDeliveryData = {};
              })
            );
          } else if (response?.data?.status_code === 422 && response?.data?.errors) {
            Object.keys(response?.data?.errors).forEach((field) => {
              formik.setFieldError(field, response?.data?.errors[field]);
            });
            dispatch(
              updateConfig((state) => {
                state.isLoading = false;
              })
            );
          } else {
            toast.error(`Something went wrong!`);
            handleCreateClick();
            dispatch(
              updateConfig((state) => {
                state.clearSelection = true;
                state.isLoading = false;
              })
            );
          }
        });
      }
    },
  });

  const getFieldError = (fieldName) => {
    if (formik.touched[fieldName] && formik.errors[fieldName]) {
      return formik.errors[fieldName];
    }
    return "";
  };
  const closeModal = () => {
    dispatch(
      updateConfig((state) => {
        state.showCreateModal = false;
        state.isEdit = false;
        state.editOfficeDeliveryData = {};
      })
    );
  };

  useEffect(() => {
     const selectedStores = basicData?.data?.stores.filter((store) =>
      editOfficeDeliveryData?.customer_keys?.includes(store?.customer_key)
    );
    if (isEdit && editOfficeDeliveryData) {
      formik.setValues({
        office_name: editOfficeDeliveryData?.office_name || "",
        alias_office_name: editOfficeDeliveryData?.alias_office_name || "",
        status: editOfficeDeliveryData?.status || 2,
        stores: selectedStores,
        office_address: editOfficeDeliveryData?.office_address || "",
        alias_office_address: editOfficeDeliveryData?.alias_office_address || "",
      });
    }
    // eslint-disable-next-line
  }, [isEdit, editOfficeDeliveryData, basicLoading]);

  const handleTypeChange = (selectedOptions) => {
    formik?.setFieldValue("stores", selectedOptions);
  };

  return {
    isEdit,
    formik,
    isLoading,
    basicData,
    editOfficeDeliveryData,
    handleTypeChange,
    getFieldError,
    closeModal,
  };
};

export default useAddOfficeDelivery;
